import { toast } from "react-toastify";

import OrganizationFunctionsService from "src/modules/data-protection/apis/OrganizationFunctionsService";
import {
    ORGANIZATION_FUNCTION_ERROR_CODES,
    extractOrganizationFunction,
    getOrganizationFunctionErrorCode,
    isDpo,
    toastOrganizationFunctionError,
} from "src/modules/data-protection/constants/organizationFunctions";
import { fetchUsers } from "src/pages/Admin/administration/user-management/api/apiFunctions";

import {
    DATA_PROTECTION_MODULE,
    diffFunctionAssignments,
    extractCreatedUsers,
    extractFunctions,
    extractUsersFromList,
    findCreatedUserId,
    getAssignedFunctionKeysForUser,
    getSelectedKeys,
    toUserId,
} from "./helpers";

export const resolveUserIdByEmail = async ({ token, email }) => {
    const normalizedEmail = String(email || "")
        .trim()
        .toLowerCase();

    if (!normalizedEmail) {
        return null;
    }

    const list = await fetchUsers({
        token,
        activeModule: DATA_PROTECTION_MODULE,
        payload: {
            pageIndex: 1,
            pageSize: 1000,
            name: email,
        },
    });

    const users = extractUsersFromList(list);
    const match = users.find(
        (user) =>
            String(user?.email || "")
                .trim()
                .toLowerCase() === normalizedEmail
    );

    return match?.id ?? null;
};

const assignOrReplaceDpo = async (service, userId) => {
    const data = await service.show("dpo");
    const organizationFunction = extractOrganizationFunction(data);
    const assignedId = toUserId(organizationFunction?.assigned_users?.[0]?.id);

    if (assignedId == null) {
        try {
            return await service.assign("dpo", userId);
        } catch (error) {
            if (
                getOrganizationFunctionErrorCode(error) ===
                ORGANIZATION_FUNCTION_ERROR_CODES.DPO_ALREADY_ASSIGNED
            ) {
                return await service.replaceDpo(userId);
            }

            throw error;
        }
    }

    if (assignedId === toUserId(userId)) {
        return;
    }

    return service.replaceDpo(userId);
};

export const syncUserFunctionAssignments = async ({
    userId,
    selectedKeys = [],
    previousKeys,
    t,
} = {}) => {
    const service = OrganizationFunctionsService.getInstance();
    let previous = previousKeys;

    if (previous == null) {
        try {
            const data = await service.list();
            previous = getAssignedFunctionKeysForUser(
                extractFunctions(data),
                userId
            );
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while updating Function assignments."
            );

            return false;
        }
    }

    const { toAssign, toUnassign } = diffFunctionAssignments({
        previousKeys: previous,
        nextKeys: selectedKeys,
    });

    let failed = false;

    for (const key of toUnassign) {
        try {
            await service.unassign(key, userId);
        } catch (error) {
            failed = true;

            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while updating Function assignments."
            );
        }
    }

    for (const key of toAssign) {
        try {
            if (isDpo(key)) {
                await assignOrReplaceDpo(service, userId);
            } else {
                await service.assign(key, userId);
            }
        } catch (error) {
            failed = true;

            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toastOrganizationFunctionError(
                error,
                t,
                "An error occurred while updating Function assignments."
            );
        }
    }

    return !failed;
};

export const assignFunctionsAfterUserCreate = async ({
    items = [],
    createResponse,
    token,
    t,
} = {}) => {
    const createdUsers = extractCreatedUsers(createResponse);

    for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const selectedKeys = getSelectedKeys(item?.functions);

        if (!selectedKeys.length) {
            continue;
        }

        let userId = findCreatedUserId({
            createdUsers,
            email: item.email,
            index,
        });

        if (userId == null) {
            try {
                userId = await resolveUserIdByEmail({
                    token,
                    email: item.email,
                });
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }
            }
        }

        if (userId == null) {
            toast(t("An error occurred while updating Function assignments."), {
                type: "error",
            });
            continue;
        }

        await syncUserFunctionAssignments({
            userId,
            selectedKeys,
            previousKeys: [],
            t,
        });
    }
};
